// Standard action names: the logical inputs tutorial prompts, binding presets, input accessibility and features
// assume (a "press interact" prompt works in any game that calls it `interact`). Each names its value type (as
// @rpg/runtime's input contract does: a press, a stick, a pointer's motion) and the device families it should be
// bound for. A game binds them in its own input contract; this list only fixes the names and shapes.
import { checkVocabulary, resolveName, type VocabularyIssue, type VocabularyOptions } from "./vocabulary.js";

/** The action vocabulary's version. */
export const ACTIONS_CONTRACT_VERSION = "1.0.0" as const;

/** An action's value: a press (0..1), a 2D direction in the unit disc (a stick), or accumulated 2D motion (look). */
export type ActionKind = "button" | "axis2" | "delta2";

/** The device families a game binds for (@rpg/runtime's `DeviceFamily`). */
export type ActionDevice = "keyboardMouse" | "gamepad" | "touch";

/** Where an action is read: in play, in a fight, in menus, in conversation, or always (system). */
export type ActionContext = "play" | "combat" | "menu" | "dialogue" | "system";

/** One standard action. */
export interface StandardAction {
  id: string;
  kind: ActionKind;
  context: ActionContext;
  /** The families it should be bound for. A family left out is normally handled another way (touch taps a screen's
   *  buttons directly, so `confirm` needs no touch binding). */
  devices: readonly ActionDevice[];
  /** What it means, for prompts and docs. */
  meaning: string;
}

const ALL: readonly ActionDevice[] = ["keyboardMouse", "gamepad", "touch"];
const KEYS_AND_PAD: readonly ActionDevice[] = ["keyboardMouse", "gamepad"];

/** The standard actions, in a fixed order. */
export const STANDARD_ACTIONS = [
  { id: "move", kind: "axis2", context: "play", devices: ALL, meaning: "Walk or run in a direction (a stick, WASD, a touch stick)" },
  { id: "look", kind: "delta2", context: "play", devices: ALL, meaning: "Turn the camera (the mouse, the right stick, a drag)" },
  { id: "jump", kind: "button", context: "play", devices: ALL, meaning: "Jump" },
  { id: "sprint", kind: "button", context: "play", devices: ALL, meaning: "Run faster while held (or toggled)" },
  { id: "crouch", kind: "button", context: "play", devices: ALL, meaning: "Crouch or sneak" },
  { id: "dodge", kind: "button", context: "play", devices: ALL, meaning: "Dodge or roll" },
  { id: "attack", kind: "button", context: "play", devices: ALL, meaning: "The basic attack, or use the held tool" },
  { id: "heavyAttack", kind: "button", context: "play", devices: ALL, meaning: "A heavy or charged attack" },
  { id: "block", kind: "button", context: "play", devices: ALL, meaning: "Block or guard while held (or toggled)" },
  { id: "skill", kind: "button", context: "play", devices: ALL, meaning: "The equipped skill" },
  { id: "ultimate", kind: "button", context: "play", devices: ALL, meaning: "The big move a meter fills" },
  { id: "lockOn", kind: "button", context: "combat", devices: ALL, meaning: "Lock the camera on a target, or switch target" },
  { id: "switchWeapon", kind: "button", context: "play", devices: ALL, meaning: "Change the equipped weapon or tool" },
  { id: "useItem", kind: "button", context: "play", devices: ALL, meaning: "Use the selected item (a potion, a seed)" },
  { id: "interact", kind: "button", context: "play", devices: ALL, meaning: "Use the nearest valid thing: talk, open, pick up" },
  { id: "map", kind: "button", context: "play", devices: ALL, meaning: "Open the map" },
  { id: "inventory", kind: "button", context: "play", devices: ALL, meaning: "Open the inventory" },
  { id: "journal", kind: "button", context: "play", devices: ALL, meaning: "Open the quest journal" },
  { id: "pause", kind: "button", context: "system", devices: ALL, meaning: "Pause and open the pause menu" },
  { id: "navigate", kind: "axis2", context: "menu", devices: KEYS_AND_PAD, meaning: "Move the focus in a menu" },
  { id: "confirm", kind: "button", context: "menu", devices: KEYS_AND_PAD, meaning: "Choose the focused item" },
  { id: "cancel", kind: "button", context: "menu", devices: KEYS_AND_PAD, meaning: "Go back, close the screen" },
  { id: "tabNext", kind: "button", context: "menu", devices: KEYS_AND_PAD, meaning: "The next tab" },
  { id: "tabPrev", kind: "button", context: "menu", devices: KEYS_AND_PAD, meaning: "The previous tab" },
  { id: "advance", kind: "button", context: "dialogue", devices: KEYS_AND_PAD, meaning: "Show the next line, or skip the typing" },
] as const satisfies readonly StandardAction[];

/** A standard action's id. */
export type StandardActionId = (typeof STANDARD_ACTIONS)[number]["id"];

const BY_ID = new Map<string, StandardAction>(STANDARD_ACTIONS.map((a) => [a.id, a]));
const IDS = new Set(BY_ID.keys());

/** The standard action of that id, if any. */
export function standardAction(id: string): StandardAction | undefined {
  return BY_ID.get(id);
}

/** A game's action as the check reads it (spike 002's `actions` rows have this shape). */
export interface GameAction {
  id: string;
  kind: ActionKind;
}

/**
 * Check a game's actions against the standard list: each id is standard, an alias of one (`{ back: "cancel" }`) or
 * the game's own; a standard (or aliased) action has the standard's kind.
 */
export function checkActions(actions: readonly GameAction[], o: VocabularyOptions = {}): VocabularyIssue[] {
  const issues = checkVocabulary("action", IDS, actions.map((a) => a.id), o);
  for (const a of actions) {
    const r = resolveName(IDS, a.id, o);
    if (r.as !== "standard" && r.as !== "alias") continue;
    const std = BY_ID.get(r.id)!;
    if (std.kind !== a.kind) issues.push({ id: a.id, kind: "mismatch", message: `action "${a.id}" is a ${a.kind}; the standard "${std.id}" is a ${std.kind}` });
  }
  return issues;
}
